import {mapGetters, mapMutations} from 'vuex';
import productQuestion from "@/components/popups/product-question/index.vue";
import productComment from "@/components/popups/product-comment/index.vue";
import registrationPopup from "@/components/popups/registration/index.vue";
import thanksPopup from "@/components/popups/thanks-popup/index.vue";
import availabilityNotify from "@/components/popups/availability-notify/index.vue"
import calculatorMessage from "@/components/popups/calculator-message/index.vue";
import trialBatch from "@/components/popups/trial-batch/index.vue";
import authPopup from '@/components/popups/auth/index.vue'
import orderColor from "@/components/popups/order-color/index.vue";
import profileDocument from "@/components/popups/profile-document/index.vue";
import specificationForm from "@/components/popups/specification-form/index.vue";
import purchaseMixin from '@/mixins/purchaseMixin';
import {
    consumeVerifiedPurchaseOrderId,
    resolvePendingPurchaseOrderId,
} from '@/utils/pendingPurchaseOrder';
import {trackPurchaseForOrderId} from '@/utils/purchasePayload';
import { logPurchaseDebug } from '@/utils/purchaseDebug';

export default {
    name: 'hidden',
    mixins: [purchaseMixin],
    data() {
        return {};
    },
    components: {
        productQuestion,
        productComment,
        registrationPopup,
        thanksPopup,
        availabilityNotify,
        calculatorMessage,
        trialBatch,
        authPopup,
        orderColor,
        profileDocument,
        specificationForm,
    },
    computed: {
        ...mapGetters({
            showQuestionPopup: 'card/showQuestionPopup',
            questionId: 'card/questionId',
            showCommentPopup: 'card/showCommentPopup',
            showRegisterPopup: 'auth/showRegisterPopup',
            showThanksPopup: 'setting/showThanksPopup',
            showSuccessPopup: 'order/showSuccessPopup',
            showAvailabilityNotifyPopup: 'order/showAvailabilityNotifyPopup',
            isShowCalculatorMessage: 'constructors/messageContent',
            showSpecificationFormPopup: 'constructors/showSpecificationFormPopup',
            showSuccessPaymentPopup: 'order/showSuccessPaymentPopup',
            orderTrialBatch: 'landings/orderTrialBatch',
            showAuthPopup: 'auth/showAuthPopup',
            showAuthThanksPopup: 'auth/showAuthThanksPopup',
            showOrderColor: 'order/orderColorPopup',
            documentPopup: 'profile/documentPopup',
            showConsultationPopup: 'catalog/showConsultationPopup'
        })
    },
    watch: {
        showSuccessPopup(newValue) {
            console.info('[GA][purchase][popup-state]', {
                popup: 'showSuccessPopup',
                isOpen: !!newValue,
                route: this.$route?.path || '',
                hasLastOrderData: !!localStorage.getItem('lastOrderData')
            });
            if (newValue) {
                this.handleSuccessPopupOpened('showSuccessPopup');
            }
        },
        showSuccessPaymentPopup(newValue) {
            console.info('[GA][purchase][popup-state]', {
                popup: 'showSuccessPaymentPopup',
                isOpen: !!newValue,
                route: this.$route?.path || '',
                hasLastOrderData: !!localStorage.getItem('lastOrderData')
            });
            if (newValue) {
                this.handleSuccessPopupOpened('showSuccessPaymentPopup');
            }
        }
    },
    methods: {
        ...mapMutations({
            closeQuestionPopup: 'card/SHOW_QUESTION_POPUP',
            closeCommentPopup: 'card/SHOW_COMMENT_POPUP',
            closeRegisterPopup: 'auth/SHOW_REGISTER_POPUP',
            closeThanksPopup: 'setting/SHOW_THANKS_POPUP',
            closeSuccessPopup: 'order/SHOW_SUCCESS_POPUP',
            showAvailabilityNotifyPopupMutation: 'order/SHOW_AVAILABILITY_NOTIFY_POPUP',
            showCalculatorMessage: 'constructors/SHOW_MESSAGE_POPUP',
            isShowTrialBatch: 'landings/ORDER_TRIAL_BATCH',
            isShowSuccessPaymentPopup: 'order/SHOW_SUCCESS_PAYMENT_POPUP',
            isShowAuthPopup: 'auth/SHOW_AUTH_POPUP',
            isShowAuthThanksPopup: 'auth/SHOW_AUTH_POPUP_THANKS',
            isShowOrderColor: 'order/SHOW_ORDER_COLOR_POPUP',
            isShowProfileDocument: 'profile/SET_DOCUMENT_POPUP',
            isShowConsultationPopup: 'catalog/SHOW_CONSULTATION_POPUP',
            closeSpecificationFormPopup: 'constructors/SHOW_SPECIFICATION_FORM_POPUP',
        }),
        async fetchPaymentStatus(orderId) {
            const response = await fetch(`/api/v1/order/payment-status?order_id=${encodeURIComponent(orderId)}`, {
                method: 'GET',
                headers: {'Accept': 'application/json'}
            }).catch(() => null);

            if (!response || !response.ok) {
                return null;
            }

            const payload = await response.json().catch(() => null);
            const guardData = payload?.data || {};

            return {
                isComplete: !!guardData.is_complete,
                isFailed: !!guardData.is_failed,
                isTerminal: !!guardData.is_terminal,
                shouldTrackPurchase: !!guardData.should_track_purchase,
                paymentStatusKey: guardData.payment_status_key || null,
            };
        },
        async canTrackPurchaseByOrderStatus(orderIdOverride = null) {
            try {
                const orderId = orderIdOverride || this.resolvePurchaseOrderId();

                if (!orderId) {
                    console.warn('⚠️ [PURCHASE] order_id not found, skip purchase tracking');
                    return false;
                }

                if (consumeVerifiedPurchaseOrderId(orderId)) {
                    console.info('[GA][purchase][guard] Order already verified on payment return', {orderId});
                    return true;
                }

                const status = await this.fetchPaymentStatus(orderId);
                if (!status) {
                    console.warn('⚠️ [PURCHASE] payment status guard request failed, skip purchase', {
                        orderId,
                    });
                    return false;
                }

                console.info('[GA][purchase][guard-result]', {
                    orderId,
                    paymentStatusKey: status.paymentStatusKey,
                    shouldTrackPurchase: status.shouldTrackPurchase,
                });

                // Online payments: only when backend says complete/paid.
                // Cash / cashless: should_track_purchase is true on order create.
                if (!status.shouldTrackPurchase) {
                    console.info('[GA][purchase][guard] Payment not complete, skip purchase', {
                        orderId,
                        paymentStatusKey: status.paymentStatusKey,
                    });
                }

                return !!status.shouldTrackPurchase;
            } catch (e) {
                console.error('❌ [PURCHASE] payment status guard exception', e);
                return false;
            }
        },
        resolvePurchaseOrderId() {
            const urlParams = new URLSearchParams(window.location.search);
            const orderId = urlParams.get('order-id') || urlParams.get('order_id');
            if (orderId) {
                return orderId;
            }

            const pendingOrderId = resolvePendingPurchaseOrderId();
            if (pendingOrderId) {
                return pendingOrderId;
            }

            const storedOrderData = localStorage.getItem('lastOrderData');
            if (!storedOrderData) {
                return null;
            }

            try {
                const orderData = JSON.parse(storedOrderData);
                return orderData.orderId || null;
            } catch (error) {
                console.error('❌ [PURCHASE] failed to parse lastOrderData for order_id', error);
                return null;
            }
        },
        handleSuccessPopupOpened(source) {
            if (this._purchasePopupTrackingInFlight) {
                return;
            }

            console.info('[GA][purchase][popup-opened]', {
                source,
                route: this.$route?.path || '',
                hasLastOrderData: !!localStorage.getItem('lastOrderData')
            });
            this._purchasePopupTrackingInFlight = true;
            this.trackPurchaseFromUrl().finally(() => {
                this._purchasePopupTrackingInFlight = false;
            });
        },
        /**
         * Отслеживание покупки при появлении success popup.
         * Payload: localStorage або API /order/purchase-payload.
         */
        async trackPurchaseFromUrl() {
            const orderId = this.resolvePurchaseOrderId();
            logPurchaseDebug('POPUP_TRACK_START', {
                route: this.$route?.path || '',
                orderId,
                hasLastOrderData: !!localStorage.getItem('lastOrderData'),
            });
            console.log('\n[PURCHASE] ========== ПОЧАТОК ВІДСТЕЖЕННЯ PURCHASE ==========');
            console.log('[PURCHASE] Викликано trackPurchaseFromUrl при появі success popup');

            if (!orderId) {
                logPurchaseDebug('POPUP_TRACK_SKIP_NO_ORDER', {});
                console.warn('[PURCHASE] order_id not found, skip purchase');
                console.log('[PURCHASE] ========== КІНЕЦЬ ВІДСТЕЖЕННЯ PURCHASE ==========\n');
                return;
            }

            const canTrackPurchase = await this.canTrackPurchaseByOrderStatus(orderId);
            if (!canTrackPurchase) {
                logPurchaseDebug('POPUP_TRACK_SKIP_GUARD', {orderId});
                console.warn('[PURCHASE] Payment is not confirmed on backend yet. Skip purchase event.');
                console.log('[PURCHASE] ========== КІНЕЦЬ ВІДСТЕЖЕННЯ PURCHASE ==========\n');
                return;
            }

            const sent = await trackPurchaseForOrderId(orderId);
            if (sent) {
                logPurchaseDebug('POPUP_TRACK_DONE', {orderId});
                console.log('✅ [PURCHASE] Purchase подія відправлена в Google Analytics');
            } else {
                logPurchaseDebug('POPUP_TRACK_SKIP_NO_DATA', {orderId});
                console.warn('⚠️ [PURCHASE] Не вдалося отримати purchase payload');
            }
            console.log('🎯 [PURCHASE] ========== КІНЕЦЬ ВІДСТЕЖЕННЯ PURCHASE ==========\n');
        }
    }
}
